import compact from "lodash/compact";
import forEach from "lodash/forEach";
import range from "lodash/range";
import zipObject from "lodash/zipObject";
import { TabularRolesForm } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form";
import {
  FIELD_AVAILABILITY_RANGES_KEY,
  FIELD_ID_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { ActionType, useActionsColumn } from "PFApp/hooks/activity_form/multi_roles_form/use_actions_column";
import {
  MultiActivityForm,
  RangesFieldKey,
  RoleFormKey,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { Inline, Stack } from "PFComponents/containers";
import { HorizontalScrollbar } from "PFComponents/horizontal_scrollbar";
import { StandardRangeV2, Subtemplate } from "PFCore/types";
import { CSSProperties, RefObject, useCallback, useId, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import css from "../apply_engagement_blueprint_page.module.scss";
import { ActivityRow, AvailabilityTable } from "../components/availability_table";

export type AccordionContentProps = {
  subtemplate: Subtemplate | null;
  rolesFormKey: RolesFormKey;
  leftPanelWidth: CSSProperties["width"];
  onAction: (roleFormKey: RoleFormKey, action: ActionType) => void;
  isOpen?: boolean;
};

const STICKY_SCROLLBAR_STYLE: CSSProperties = {
  position: "sticky",
  bottom: 64 // PageFooter height
};

export const AccordionContent = ({
  subtemplate,
  rolesFormKey,
  leftPanelWidth,
  onAction,
  isOpen
}: AccordionContentProps) => {
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const leftPanelId = useId();
  const rightPanelId = useId();

  const { setValue } = useFormContext<MultiActivityForm>();

  const roleFormKeys = useWatch<MultiActivityForm, RolesFormKey>({
    name: rolesFormKey,
    defaultValue: [],
    compute: (roles) => range(roles.length).map((index) => `${rolesFormKey}.${index}`)
  }) as RoleFormKey[];

  const rangesValues = useWatch<MultiActivityForm, RangesFieldKey[]>({
    name: roleFormKeys.map((key) => `${key}.${FIELD_AVAILABILITY_RANGES_KEY}` as RangesFieldKey)
  });

  const disabledRoleFormKeys = useWatch<MultiActivityForm, RolesFormKey, unknown, RoleFormKey[]>({
    name: rolesFormKey,
    defaultValue: [],
    compute: (roles) =>
      compact(
        roles.map((role, index) => {
          const isCreated = !!role[FIELD_ID_KEY];
          return isCreated ? `${rolesFormKey}.${index}` : null;
        })
      ) as RoleFormKey[]
  });

  const generateActionColumn = useActionsColumn<ActivityRow>({
    roleFormKeys,
    disableIfRoleExists: true,
    onChange: onAction,
    actionsBlacklist: [ActionType.EditAvailability]
  });

  const availabilityTableValue = useMemo(
    () => zipObject(roleFormKeys, rangesValues),
    [roleFormKeys, rangesValues]
  );

  const handleAvailabilityChange = useCallback(
    (availability: Record<RoleFormKey, StandardRangeV2[]>) => {
      forEach(availability, (availabilityRules, roleFormKey) => {
        setValue(`${roleFormKey}.${FIELD_AVAILABILITY_RANGES_KEY}` as RangesFieldKey, availabilityRules, {
          shouldDirty: true
        });
      });
    },
    [setValue]
  );

  const getPanelWrapperStyle = useCallback(
    (ref: RefObject<HTMLElement | null>): Partial<CSSStyleDeclaration> | undefined => {
      const panel = ref.current;
      if (!panel) {
        return;
      }
      const rect = panel.getBoundingClientRect();
      return { left: `${rect.left + panel.clientLeft}px`, width: `${panel.clientWidth}px` };
    },
    []
  );

  const getLeftHeaderWrapperStyle = useCallback(
    () => getPanelWrapperStyle(leftPanelRef),
    [getPanelWrapperStyle]
  );

  const getRightHeaderWrapperStyle = useCallback(
    () => getPanelWrapperStyle(rightPanelRef),
    [getPanelWrapperStyle]
  );

  return (
    <Inline className={css.accordionContent} gap={null}>
      <Stack gap={null} className={css.leftPanelWrapper} style={{ width: leftPanelWidth }}>
        <div
          ref={leftPanelRef}
          id={leftPanelId}
          className={css.leftPanel}
          data-qa-id={`${subtemplate?.subtemplate_key ?? NO_SUBTEMPLATE_VALUE}.AccordionContent.LeftPanel`}
        >
          <TabularRolesForm
            subtemplate={subtemplate ?? null}
            rolesFormKey={rolesFormKey}
            checkDisabled={(role) => !!role[FIELD_ID_KEY]} // MVP: Do not allow editing roles
            scrollableContainerRef={leftPanelRef}
            getFixedHeaderWrapperStyle={getLeftHeaderWrapperStyle}
          />
        </div>
        <HorizontalScrollbar
          scrollableContainerRef={leftPanelRef}
          contentRef={leftPanelRef}
          getContentWidth={() => leftPanelRef.current?.scrollWidth ?? 0}
          getWindowWidth={() => leftPanelRef.current?.clientWidth ?? 0}
          style={STICKY_SCROLLBAR_STYLE}
        />
      </Stack>
      <Stack gap={null} className={css.rightPanelWrapper}>
        <div
          ref={rightPanelRef}
          id={rightPanelId}
          className={css.rightPanel}
          data-qa-id={`${subtemplate?.subtemplate_key ?? NO_SUBTEMPLATE_VALUE}.AccordionContent.RightPanel`}
        >
          <AvailabilityTable
            key={roleFormKeys.length} // easist way to sync when role is added/removed
            roleFormKeys={roleFormKeys}
            value={availabilityTableValue}
            disabledRoleFormKeys={disabledRoleFormKeys} // MVP: Do not allow editing roles
            onRowsChange={handleAvailabilityChange}
            generateActionsColumn={generateActionColumn}
            scrollableContainerRef={rightPanelRef}
            getFixedHeaderWrapperStyle={getRightHeaderWrapperStyle}
            isOpen={isOpen}
          />
        </div>
        <HorizontalScrollbar
          scrollableContainerRef={rightPanelRef}
          contentRef={rightPanelRef}
          getContentWidth={() => rightPanelRef.current?.scrollWidth ?? 0}
          getWindowWidth={() => rightPanelRef.current?.clientWidth ?? 0}
          style={STICKY_SCROLLBAR_STYLE}
        />
      </Stack>
    </Inline>
  );
};
